import { useLayoutEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useAppConfig } from "@ct-react/core";
import { Wrapper } from "./wrapper";
import { LocaleLink, useLocaleContext, useLocaleFormatter } from "@ct-react/locale";
import SpriteSvg from "../common/svg";
import { Facebook, Instagram, WhatsApp } from "../common/icones";
import Logo from "../../assets/componable-svgs/logo-verbier-rentals-fond-blanc.svg";
import { useElementSize } from "../../hooks/dom";
import { articleTypes, form, routes } from "../../i18n/sharable-defs";
import "./footer.scss";
import { ArticleType } from "@shared/url";

const footerMessages = defineMessages({
  recaptcha: {
    id: "footer.recaptcha",
    defaultMessage: "Ce site est protégé par reCAPTCHA. Les <privacyPolicy>règles de confidentialité</privacyPolicy> et les <termsOfService>conditions d'utilisation</termsOfService> de Google s'appliquent."
  }
});

const Footer = () => {

  const intl = useIntl();
  const { options: { recaptchaSiteKey } } = useAppConfig();
  const { locale } = useLocaleContext();
  const year = useRef<number>(new Date().getFullYear());
  const { transLib } = useLocaleFormatter();

  const { ref, size } = useElementSize();

  useLayoutEffect(() => {
    !!size?.height && document.documentElement.style.setProperty("--cgv-footer-height", `${size.height}px`);
  }, [ size?.height ]);

  return (
    <footer ref={ref}
            className="main-footer">

      <Wrapper.Centered>
        <div>
          <LocaleLink to={"/"} className="brand keep">
            <Logo/>
          </LocaleLink>
          <div className="social-icones">
            <Instagram className="primary"/>
            <Facebook className="primary"/>
            <WhatsApp className="primary"/>
          </div>
        </div>
        <address>
          <p>
            <SpriteSvg id="localisation-line"/>
            {intl.formatMessage(form.address)}
          </p>
          <a href="https://www.google.com/maps/place/Verbier+Rentals+S%C3%A0rl/@46.0952351,7.2256363,754m/data=!3m2!1e3!4b1!4m6!3m5!1s0x478ece3357a5ef7b:0x27632d4a9acffc6c!8m2!3d46.0952314!4d7.2282112!16s%2Fg%2F1th1bmnt?entry=ttu&g_ep=EgoyMDI1MTEyMy4xIKXMDSoASAFQAw%3D%3D" aria-label="Check the itinerary" target="_blank">
            Verbier Rentals Sàrl<br/>
            Chemin des Vernes 5a<br/>
            CH - 1936 Verbier
          </a>
        </address>
        <address>
          <p>
            <SpriteSvg id="mail-line"/>
            Contact
          </p>
          {intl.formatMessage(form.booking)} : <a href="tel:+41794876948" aria-label="Call for bookings">+41 79 487 69 48</a><br/>
          {intl.formatMessage(form.inResort)} : <a href="tel:+41799168260" aria-label="Call for in resort">+41 79 916 82 60</a><br/>
          {intl.formatMessage(articleTypes[ArticleType.SALE])} : <a href="tel:+41794509704" aria-label="Call for sales">+41 79 450 97 04</a><br/>
          <a href="mailto:info@verbierrentals.com" aria-label="Send us an email !">info@verbierrentals.com</a>
        </address>
      </Wrapper.Centered>

      <Wrapper.Centered>
        <nav>
          <a href={`/public/terms-and-conditions-${locale.basename}.pdf`} target="_blank">{intl.formatMessage(routes.conditions)}</a>
          <span>|</span>
          <a href={`/public/legal-notice-${locale.basename}.pdf`} target="_blank">{intl.formatMessage(routes.notice)}</a>
        </nav>
        <span className="copyright">
          {intl.formatMessage({
            id: "footer.copyright",
            defaultMessage: "© {year} {agency} tous droits réservés | développé par {ct}"
          }, {
            year: year.current,
            agency : "Verbier Rentals",
            ct: <a key="ct" href="https://www.creative-technologies.ch" target="_blank">C.T. Creative Technologies SA</a>
          })}
        </span>
        {!!recaptchaSiteKey &&
          <span className="recaptcha-notice">
            {intl.formatMessage(footerMessages.recaptcha, {
              privacyPolicy: chunks =>
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">{chunks}</a>,
              termsOfService: chunks =>
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">{chunks}</a>
            })}
          </span>
        }
      </Wrapper.Centered>

    </footer>);

}

export default Footer;
